import Search from "@mui/icons-material/Search";
import { Box, CircularProgress, TextField, Typography } from "@mui/material";
import { useDebouncedCallback } from "@react-hookz/web";
import type { FC } from "react";
import { useRef, useState } from "react";
import { useToken } from "../../server/auth/use-token";
import { authHeader } from "../../utils/auth-header";
import { selfDriving } from "../self-driving/self-driving";
import { UnsplashImageAttribution } from "./unsplash-image-attribution";

type PhotosResponse = {
  photos: {
    url: string;
    orientation: string;
    attribution?: {
      name?: string;
      unsplash_profile_url?: string;
    };
  }[];
};

const getAnalyticsPrefix = (endpoint: "/photos/stock" | "/photos/vector"): string => {
  return endpoint === "/photos/stock" ? "sidebar-unsplash-image" : "sidebar-curated-image";
};

export const PhotosTab: FC<{
  endpoint: "/photos/stock" | "/photos/vector";
  placeholder: string;
  onImageClick?: (imageUrl: string) => void;
  selectedImage: string | null;
  onSelectionChange?: (hasSelection: boolean) => void;
  hidden?: boolean;
}> = ({
  endpoint,
  placeholder,
  onImageClick,
  selectedImage,
  onSelectionChange,
  hidden = false,
}) => {
  const analyticsPrefix = getAnalyticsPrefix(endpoint);
  const getToken = useToken();
  const [searchResults, setSearchResults] = useState<PhotosResponse["photos"]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleImageClick = (url: string): void => {
    onImageClick?.(url);
    onSelectionChange?.(true);
  };

  const handleSearch = useDebouncedCallback(
    async (query: string) => {
      const trimmedQuery = query.trim();

      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (!trimmedQuery) {
        setSearchResults([]);
        setHasSearched(false);
        return;
      }

      // Create new abort controller for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsLoading(true);
      setHasSearched(true);
      try {
        const token = await getToken();

        const { data, error } = await selfDriving.GET(endpoint, {
          params: {
            query: { query: trimmedQuery },
          },
          ...authHeader(token),
          signal: abortController.signal,
        });

        if (error || !data) throw new Error(error?.error || "Request failed");

        setSearchResults(data.photos);
      } catch (error) {
        // Ignore abort errors
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        console.error("Search failed:", error);
      } finally {
        // Only set loading to false if this request wasn't aborted
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [getToken, endpoint],
    300,
  );

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        px: 3,
        py: 2.5,
        backgroundColor: "background.default",
        color: "text.secondary",
        display: hidden ? "none" : "block",
      }}
    >
      <Typography variant="body2" sx={{ mb: 2 }}>
        {endpoint === "/photos/stock"
          ? "Wide range of choices. Quality of result may vary."
          : "Picked by our designers. May not work for niche topics."}
      </Typography>

      <TextField
        fullWidth
        placeholder={placeholder}
        size="small"
        onChange={(e) => handleSearch(e.target.value)}
        sx={{ mb: 2, backgroundColor: "white" }}
        slotProps={{
          input: {
            endAdornment: <Search sx={{ color: "text.secondary" }} />,
          },
        }}
      />

      {searchResults.length > 0 && !isLoading && (
        <Typography variant="overline" color="text.secondary">
          Current images
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              gridColumn: "1 / -1",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        ) : hasSearched && searchResults.length === 0 ? (
          <Box
            sx={{
              gridColumn: "1 / -1",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No results
            </Typography>
          </Box>
        ) : (
          searchResults.map((photo, index) => (
            <Box
              key={index}
              data-analytics-id={`${analyticsPrefix}-${index}`}
              onClick={() => handleImageClick(photo.url)}
              sx={(theme) => ({
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: "background.default",
                border:
                  selectedImage === photo.url
                    ? `3px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                aspectRatio: "3 / 4",
                position: "relative",
                cursor: "pointer",
                "&:hover": {
                  borderColor: theme.palette.primary.light,
                },
                "&:hover .unsplash-attribution": {
                  opacity: 1,
                },
              })}
            >
              <Box
                component="img"
                src={photo.url}
                alt=""
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              {photo?.attribution?.name && (
                <UnsplashImageAttribution
                  name={photo.attribution.name}
                  url={photo.attribution.unsplash_profile_url}
                />
              )}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};
